import {
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { OrderFlowModelChoiceComponent } from './order-flow-model-choice.component';
import { OrderFlowService } from '../order-flow.service';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { OrderDetailItem } from '../../../model/order-detail';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-order-flow-product-choice',
  templateUrl: './order-flow-product-choice.component.html',
  styleUrls: ['./order-flow-product-choice.component.css'],
  standalone: true,
  imports: [OrderFlowModelChoiceComponent, CommonModule, ReactiveFormsModule],
})
export class OrderFlowProductChoiceComponent implements OnInit {
  @ViewChild(OrderFlowModelChoiceComponent)
  modelChoiceComponent!: OrderFlowModelChoiceComponent;

  get configurationForm(): FormGroup {
    return this.modelChoiceComponent.configurationForm;
  }

  products: any[] = [];
  selectedProductId: number | null = null;
  selectedModelIdFromDuplicateAction: number | null = null;
  data: any;
  orderDetail: any;
  models: any[] = [];
  attributes: any[] = [];
  options: any[] = [];

  originalProductId: number | null = null;
  originalModelId: number | null = null;
  originalAttributeOptions: { attributeOptionId: number; valueId: number }[] = [];
  productNameSelected = "";

  @Input() isDuplicateAction = false;
  @Input() baseOrderNumber = '';

  progress = 0; // Inicialmente o progresso é 0
  progressText = `${this.progress}% Done`;

  constructor(private orderFlowService: OrderFlowService) {}

  ngOnInit(): void {
    if (!this.isDuplicateAction) {
      this.orderFlowService.getProductChoice().subscribe((data) => {
        this.data = data.data;
        this.products = this.sortByOrder(data.data.products);
        this.clearAttributeOptionsBrackets();
      });
    }
  }

  clearAttributeOptionsBrackets(): void {
    if (environment && environment.production) {
      if (this.data.attributes.length > 0) {
        this.data.attributes.forEach((attribute: any) => {
          if (attribute.name && attribute.name.includes('(')) {
            const match = attribute.name.match(/\((\d+)\)/);
            if (!match) {
              attribute.name = attribute.name.split('(')[0].trim();
            }
          }
        });
      }

      if (this.data.options.length > 0) {
        this.data.options.forEach((option: any) => {
          if (option.name && option.name.includes('(')) {
            const match = option.name.match(/\((\d+)\)/);
            if (!match) {
              option.name = option.name.split('(')[0].trim();
            }
          }
        });
      }
    }
  }

  onSelectProduct(productId: number): void {
    this.clearAttributeOptionsBrackets();
    this.selectedProductId = productId;

    this.models = this.data.models
      .filter((model: any) => model.productId === productId)
      .sort(this.compareOrder);

    this.attributes = this.data.attributes
      .filter(
        (attribute: { productId: number }) => attribute.productId === productId,
      )
      .sort(this.compareOrder);
    this.options = this.data.options
      .filter((option: { productId: number }) => option.productId === productId)
      .sort(this.compareOrder);

    this.updateProgress(30);
  }

  /**
   * Populates the product choice component with duplicated data.
   * @param duplicatedData The duplicated data payload.
   */
  populateProductChoice(duplicatedData: any): void {
    // Assuming duplicatedData.products contains selected values
    // Find the product with a selectedValue

    const selectedProduct = duplicatedData.products.find(
      (product: any) => product.selectedValue !== null,
    );

    if (selectedProduct) {
      this.selectedProductId = selectedProduct.productId;
      this.originalProductId = selectedProduct.productId;

      // Initialize models, attributes, and options based on the selected product
      this.models = this.data.models
        .filter((model: any) => model.productId === selectedProduct.productId)
        .sort(this.compareOrder);

      this.modelChoiceComponent.models = this.models;

      this.attributes = this.data.attributes
        .filter(
          (attribute: { productId: number }) =>
            attribute.productId === selectedProduct.productId,
        )
        .sort(this.compareOrder);

      this.modelChoiceComponent.attributes = this.attributes;

      this.options = this.data.options
        .filter(
          (option: { productId: number }) =>
            option.productId === selectedProduct.productId,
        )
        .sort(this.compareOrder);

      this.modelChoiceComponent.options = this.options;

      this.updateProgress(30);

      const selectedModel = this.data.models.find(
        (model: any) => model.selectedValue !== null,
      );

      // Populate selected model if available
      if (selectedModel) {
        this.selectedModelIdFromDuplicateAction = selectedModel.modelId;
        this.originalModelId = selectedModel.modelId;

        this.modelChoiceComponent.populateModelSelection(selectedModel.modelId);
        this.updateProgress(60);
      }

      // Store original attribute options
      this.originalAttributeOptions = this.modelChoiceComponent.attributeOptions.map(option => ({
        attributeOptionId: option.attributeOptionId,
        valueId: option.valueId,
      }));

      // Populate selected attributes and options if available
      if (selectedProduct.selectedAttributes) {
        // this.modelChoiceComponent.populateAttributes(selectedProduct.selectedAttributes);
        this.updateProgress(80);
      }

      if (selectedProduct.selectedOptions) {
        // this.modelChoiceComponent.populateOptions(selectedProduct.selectedOptions);
        this.updateProgress(100);
      }
    }
  }

  hasChanges(): boolean {
    // Compare current selections with original selections
    if (this.selectedProductId !== this.originalProductId) {
      return true;
    }
    if (this.modelChoiceComponent.selectedModelId !== this.originalModelId) {
      return true;
    }
    // Compare attribute options
    if (!this.arraysEqual(this.modelChoiceComponent.attributeOptions, this.originalAttributeOptions)) {
      return true;
    }
    return false;
  }

  arraysEqual(a1: { attributeOptionId: number; valueId: number }[], a2: { attributeOptionId: number; valueId: number }[]): boolean {
    if (a1.length !== a2.length) return false;
    return a1.every((item, index) => {
      const otherItem = a2[index];
      return item.attributeOptionId === otherItem.attributeOptionId && item.valueId === otherItem.valueId;
    });
  }

  /**
   * Utility method to sort an array of items by their 'order' property.
   * Assumes that the items have an 'order' property of type number.
   */
  sortByOrder<T extends { order: number }>(items: T[]): T[] {
    return [...items].sort(this.compareOrder);
  }

  /**
   * Comparator function to sort items based on the 'order' property.
   */
  private compareOrder(a: { order: number }, b: { order: number }): number {
    return a.order - b.order;
  }

  assembleSummaryPayload(): OrderDetailItem[] {
    const orderDetailItems: OrderDetailItem[] = [];

    if (this.selectedProductId) {
      const productName = this.getSelectedProductName();
      orderDetailItems.push({
        group: 'Product Detail',
        label: 'Range',
        value: productName,
      });
    }

    if (this.modelChoiceComponent.selectedModelId) {
      const modelName = this.modelChoiceComponent.getSelectedModelName();
      orderDetailItems.push({
        group: 'Product Detail',
        label: 'Model',
        value: modelName,
      });
    }

    // Valores de Atributos
    if (this.modelChoiceComponent.selectedValues) {
      const attributeValues = this.modelChoiceComponent.selectedValues;

      for (const attributeId in attributeValues) {
        const valueId = attributeValues[attributeId];

        const attribute = this.modelChoiceComponent.filteredAttributes.find(
          (attr) => attr.attributeOptionId === Number(attributeId),
        );

        // Encontrar o valor selecionado dentro de attribute.values
        const valueOption = attribute?.values.find(
          (val: { valueId: number }) => val.valueId === valueId,
        );

        if (attribute && valueOption) {
          orderDetailItems.push({
            group: 'Product Detail',
            label: attribute.name,
            value: valueOption.value, // Supondo que 'value' é o nome do valor selecionado
          });
        }
      }
    }

    // Valores de Opções
    if (this.modelChoiceComponent.selectedOptionValues) {
      const optionValues = this.modelChoiceComponent.selectedOptionValues;

      for (const optionId in optionValues) {
        const valueId = optionValues[optionId];

        // Encontrar a opção pelo optionId
        const option = this.modelChoiceComponent.filteredOptions.find(
          (opt) => opt.attributeOptionId === Number(optionId),
        );

        // Verificar se 'option' e 'option.values' existem
        if (option && option.values) {
          // Encontrar o valor selecionado dentro de option.values
          const valueOption = option.values.find(
            (val: { valueId: number }) => val.valueId === valueId,
          );

          if (valueOption) {
            orderDetailItems.push({
              group: 'Product Detail',
              label: option.name, // Nome da opção
              value: valueOption.value, // Nome do valor selecionado
            });
          }
        }
      }
    }

    return orderDetailItems;
  }

  updateProgress(step: number) {
    this.progress = step;
    this.progressText = `${this.progress}% Done`;
  }

  goBackToProductSelection(): void {
    this.selectedProductId = null;
    this.models = [];
    this.attributes = [];
    this.options = [];
    this.updateProgress(0);
    this.modelChoiceComponent.goBackToModelSelection();
  }

  getSelectedProductName(): string {
    const selectedProduct = this.products.find(
      (product) => product.productId === this.selectedProductId,
    );
    const result =  selectedProduct ? selectedProduct.name : '';

    this.productNameSelected = result;

    return result;
  }

buildProductChoicePayload() {
  const formValues = this.configurationForm.value;

  const isValidValue = (value: any) =>
    value !== null && value !== -1 && value !== '';

  const mapToOption = (key: string, value: any) => ({
    attributeOptionId: Number(key),
    valueId: Number(value),
  });

  const attributeOptions = Object.entries(formValues)
    .filter(([_, value]) => isValidValue(value))
    .flatMap(([key, value]) =>
      Array.isArray(value)
        ? value.map(v => mapToOption(key, v))
        : [mapToOption(key, value)]
    );

  return {
    productId: this.selectedProductId ?? 0,
    modelId: this.modelChoiceComponent.selectedModelId ?? 0,
      attributeOptions: this.modelChoiceComponent.attributeOptions,
  };
}

  
}